import AbcIcon from '@mui/icons-material/Abc';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import NumbersIcon from '@mui/icons-material/Numbers';
import RuleIcon from '@mui/icons-material/Rule';
import axios from 'axios';
import Cookies from "js-cookie";
import pkg from '../../package.json';

export const API_URL = "/imsFilesApi";

export const clearAuthentication = () => {
  Cookies.remove('kcAccessToken', { path: '/imsFilesApi' });
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
}

export function jsonBlob(obj) {
  return new Blob([JSON.stringify(obj)], {
    type: "application/json",
  });
}

export const getAccessToken = () => Cookies.get('accessToken');
export const isAuthenticated = () => !!getAccessToken();

export const getFileExtension = (fileName => {
  return fileName.split('.').pop().toLowerCase();
});

/**
 * Returns a random integer between 0 (inclusive) and max (exclusive).
 * @param {*} max 
 * @returns 
 */
export const getRandomInt = (max) => {
  return Math.floor(Math.random() * max);
}

/**
 * Debounce the specified function by the specified timeout.
 * @param {*} func the function to debounce
 * @param {*} timeout in ms, default is 300
 * @returns 
 */
export const debounce = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  }
};

/**
 * Returns the icon for the specified attribute-type.
 * @param {*} type 
 * @returns 
 */
export const getAttributeIcon = (type) => {
  let icon = <CheckBoxIcon sx={{ color: 'gray' }} />;
  switch (type) {
    case 'NUMBER':
      icon = <NumbersIcon sx={{ color: 'gray' }} />;
      break;
    case 'TEXT':
      icon = <AbcIcon sx={{ color: 'gray' }} />;
      break;
    case 'BOOL':
      icon = <RuleIcon sx={{ color: 'gray' }} />;
      break;
    case 'SELECTION':
      icon = <DynamicFeedIcon sx={{ color: 'gray' }} />;
      break;
  }
  return icon;
}

// all language-keys
export const LOCALE_KEYS = ["cz", "de", "en", "es", "fr", "hr", "it", "ja", "nl", "pl", "pt", "ro", "rs", "ru", "tr"];

export const REGION_KEYS = ["GLOBAL", "DE", "US", "GB", "CH"];

/**
 * Returns true, if at least one language is set for the specified data-object.
 * @param {*} localization
 * @returns 
 */
export const hasLanguageSet = (localization) => {
  if (localization) {
    for (let l of LOCALE_KEYS) {
      if (localization[l] && localization[l] !== "") return true;
    }
  }
  return false;
};

/**
 * Tries to return a fitting default-language of the specified value.
 * @param {*} languages 
 * @returns 
 */
export const getDefaultLanguage = (languages, locale) => {
  if (languages[locale]) {
    return languages[locale];
  }
  if (languages['en']) {
    return languages['en'];
  }
  for (let lang of LOCALE_KEYS) {
    if (languages[lang]) {
      return languages[lang];
    }
  }
  return '';
}

/**
 * Returns true, if both language-objects are the same in all language-fields.
 * @param {*} selA 
 * @param {*} selB 
 * @returns 
 */
export const checkLanguageEquality = (selA, selB) => {
  if (!selA && !selB) return true;
  if (!selA || !selB) return false;
  let equal = true;
  for (let lang of LOCALE_KEYS) {
    if (selA[lang] !== selB[lang]) {
      equal = false;
      break;
    }
  }
  return equal;
};

/**
 * Returns the path to the flag (in SVG-format) of the specified locale.
 * @param {*} locale 
 * @returns 
 */
export const getFlagPathToLocale = (locale) => {
  let name = "de";
  switch (locale) {
    case "en":
      name = "usgb";
      break;
    default:
      name = locale;
  }
  return `/img/flags/${name}.svg`;
}

export const refreshAuthToken = () => {
  return axios
    .post(`${API_URL}/login/refresh`, undefined, { withCredentials: true });
};

/**
 * The api-endpoint to use.
 * Configured with a refresh-interceptor.
 */
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

/**
 * A mapping of IFC-classes to their respective IFC-types.
 */
export const IFC_CLASS_TYPES = {
  IfcBeam: ["BEAM", "CORNICE", "DIAPHRAGM", "EDGEBEAM", "GIRDER_SEGMENT", "HATSTONE", "HOLLOWCORE", "JOIST", "LINTEL", "PERCAP", "SPANDREL", "T_BEAM", "USERDEFINED", "NOTDEFINED"],
  IfcBearing: ["CYLINDRICAL", "DISK", "ELASTOMERIC", "GUIDE", "POT", "ROCKER", "ROLLER", "SPHERICAL", "USERDEFINED", "NOTDEFINED"],
  IfcBuildingElementProxy: ["COMPLEX", "ELEMENT", "PARTIAL", "PROVISIONFORSPACE", "PROVISIONFORVOID", "USERDEFINED", "NOTDEFINED"],
  IfcChimney: ["USERDEFINED", "NOTDEFINED"],
  IfcColumn: ["COLUMN", "PIERSTEM", "PIERSTEM_SEGMENT", "PILASTER", "STANDCOLUMN", "USERDEFINED", "NOTDEFINED"],
  IfcCovering: ["CEILING", "CLADDING", "COPING", "FLOORING", "INSULATION", "MEMBRANE", "MOLDING", "ROOFING", "SKIRTINGBOARD", "SLEEVING", "TOPPING", "WRAPPING", "USERDEFINED", "NOTDEFINED"],
  IfcCurtainWall: ["USERDEFINED", "NOTDEFINED"],
  IfcDeepFoundation: [],
  IfcDoor: ["BOOM_BARRIER", "DOOR", "GATE", "TRAPDOOR", "TURNSTILE", "USERDEFINED", "NOTDEFINED"],
  IfcFooting: ["CAISSON_FOUNDATION", "FOOTING_BEAM", "PAD_FOOTING", "PILE_CAP", "STRIP_FOOTING", "USERDEFINED", "NOTDEFINED"],
  IfcMember: ["ARCH_SEGMENT", "BRACE", "CHORD", "COLLAR", "MEMBER", "MULLION", "PLATE", "POST", "PURLIN", "RAFTER", "STAY_CABLE", "STIFFENING_RIB", "STRINGER", "STRUCTURALCABLE", "STRUT", "STUD", "SUSPENDER", "SUSPENSION_CABLE", "TIEBAR", "USERDEFINED", "NOTDEFINED"],
  IfcPlate: ["BASE_PLATE", "COVER_PLATE", "CURTAIN_PANEL", "FLANGE_PLATE", "GUSSET_PLATE", "SHEET", "SPLICE_PLATE", "STIFFENER_PLATE", "WEB_PLATE", "USERDEFINED", "NOTDEFINED"],
  IfcRailing: ["BALUSTRADE", "FENCE", "GUARDRAIL", "HANDRAIL", "USERDEFINED", "NOTDEFINED"],
  IfcRamp: ["HALF_TURN_RAMP", "QUARTER_TURN_RAMP", "SPIRAL_RAMP", "STRAIGHT_RUN_RAMP", "TWO_QUARTER_TURN_RAMP", "TWO_STRAIGHT_RUN_RAMP", "USERDEFINED", "NOTDEFINED"],
  IfcRampFlight: ["SPIRAL", "STRAIGHT", "USERDEFINED", "NOTDEFINED"],
  IfcRoof: ["BARREL_ROOF", "BUTTERFLY_ROOF", "DOME_ROOF", "FLAT_ROOF", "FREEFORM", "GABLE_ROOF", "GAMBREL_ROOF", "HIPPED_GABLE_ROOF", "HIP_ROOF", "MANSARD_ROOF", "PAVILION_ROOF", "RAINBOW_ROOF", "SHED_ROOF", "USERDEFINED", "NOTDEFINED"],
  IfcShadingDevice: ["AWNING", "JALOUSIE", "SHUTTER", "USERDEFINED", "NOTDEFINED"],
  IfcSlab: ["APPROACH_SLAB", "BASESLAB", "FLOOR", "LANDING", "PAVING", "ROOF", "SIDEWALK", "TRACKSLAB", "WEARING", "USERDEFINED", "NOTDEFINED"],
  IfcStair: ["CURVED_RUN_STAIR", "DOUBLE_RETURN_STAIR", "HALF_TURN_STAIR", "HALF_WINDING_STAIR", "LADDER", "QUARTER_TURN_STAIR", "QUARTER_WINDING_STAIR", "SPIRAL_STAIR", "STRAIGHT_RUN_STAIR", "THREE_QUARTER_TURN_STAIR", "THREE_QUARTER_WINDING_STAIR", "TWO_CURVED_RUN_STAIR", "TWO_QUARTER_TURN_STAIR", "TWO_QUARTER_WINDING_STAIR", "TWO_STRAIGHT_RUN_STAIR", "USERDEFINED", "NOTDEFINED"],
  IfcStairFlight: ["CURVED", "FREEFORM", "SPIRAL", "STRAIGHT", "WINDER", "USERDEFINED", "NOTDEFINED"],
  IfcWall: ["ELEMENTEDWALL", "MOVABLE", "PARAPET", "PARTITIONING", "PLUMBINGWALL", "POLYGONAL", "RETAININGWALL", "SHEAR", "SOLIDWALL", "STANDARD", "WAVEWALL", "USERDEFINED", "NOTDEFINED"],
  IfcWindow: ["LIGHTDOME", "SKYLIGHT", "WINDOW", "USERDEFINED", "NOTDEFINED"],
  IfcOpeningElement: ["OPENING", "RECESS", "USERDEFINED", "NOTDEFINED"],
  IfcSpaceHeater: ["CONVECTOR", "RADIATOR", "USERDEFINED", "NOTDEFINED"],
  IfcSpace: ["BERTH", "EXTERNAL", "GFA", "INTERNAL", "PARKING", "SPACE", "USERDEFINED", "NOTDEFINED"],
  IfcSensor: ["CO2SENSOR", "CONDUCTANCESENSOR", "CONTACTSENSOR", "COSENSOR", "EARTHQUAKESENSOR", "FIRESENSOR", "FLOWSENSOR", "FOREIGNOBJECTDETECTIONSENSOR", "FROSTSENSOR", "GASSENSOR", "HEATSENSOR", "HUMIDITYSENSOR", "IDENTIFIERSENSOR", "IONCONCENTRATIONSENSOR", "LEVELSENSOR", "LIGHTSENSOR", "MOISTURESENSOR", "MOVEMENTSENSOR", "OBSTACLESENSOR", "PHSENSOR", "PRESSURESENSOR", "RADIATIONSENSOR", "RADIOACTIVITYSENSOR", "RAINSENSOR", "SMOKESENSOR", "SNOWDEPTHSENSOR", "SOUNDSENSOR", "TEMPERATURESENSOR", "TRAINSENSOR", "TURNOUTCLOSURESENSOR", "WHEELSENSOR", "WINDSENSOR", "USERDEFINED", "NOTDEFINED"],
  IfcUnitaryEquipment: ["AIRCONDITIONINGUNIT", "AIRHANDLER", "DEHUMIDIFIER", "ROOFTOPUNIT", "SPLITSYSTEM", "USERDEFINED", "NOTDEFINED"]
};

/**
 * Returns a list of associated types for a given IFC class name.
 *
 * @param ifcClassName The name of the IFC class (e.g., "IfcBeam").
 * @returns An array of associated type strings, or an empty array if the class name is not found.
 */
export const getIfcTypes = (ifcClass) => {
  return IFC_CLASS_TYPES[ifcClass] ?? [];
}

/**
 * All defined AI-classes.
 */
export const AI_CLASSES = [
  "Alarm_Activator", "Fire_Blanket", "Fire_Exit", "Fire_Extinguisher", "Fire_Suppression_Signage", "Flashing_Light_Orbs", "Sounders", "White_Domes",
  "Einhebelarmatur-Stand", "3-Loch-Armatur-Wand", "Zweigriffarmatur", "Handbrause", "Wannen-/Duscharmatur", "Badarmatur_Unterputz", "Einbauwaschtisch", "Aufsatzwaschtisch",
  "Standwaschtisch", "Wandwaschtisch", "Kopfbrause", "Drueckerplatte", "Badheizkoerper", "Einbauwaschtisch_rand", "Wannenarmatur_Stand", "Deckendusche", "Stabhandbrause",
  "Einhebelarmatur-Wand", "3-Loch-Armatur-Stand", "Wannen-Zweilochbatterie", "Duschkabine", "Bidet", "Badewanne", "Toilette", "Door", "Washing machine", "Bathroom cabinet",
  "Stairs", "Cabinetry", "Oven", "Power plugs and sockets", "Door handle", "Light switch", "Ceiling fan", "Fireplace", "Refrigerator", "Wood-burning stove", "Hand dryer",
  "Soap dispenser", "Bench", "Lamp", "Light bulb", "Dishwasher",
  "Window", "Radiator"
]

/**
 * All defined units with their corresponding label.
 */
export const UNIT_CHOICES = {
  'DISTANCE': [
    { value: 'MM', label: 'mm', imperial: false }, { value: 'CM', label: 'cm', imperial: false },
    { value: 'DM', label: 'dm', imperial: false }, { value: 'M', label: 'm', imperial: false },
    { value: 'KM', label: 'km', imperial: false }, { value: 'IN', label: 'in', imperial: true },
    { value: 'FT', label: 'ft', imperial: true }, { value: 'YD', label: 'yd', imperial: true },
    { value: 'MI', label: 'mi', imperial: true }
  ],
  'AREA': [
    { value: 'MM2', label: 'mm²', imperial: false }, { value: 'CM2', label: 'cm²', imperial: false },
    { value: 'DM2', label: 'dm²', imperial: false }, { value: 'M2', label: 'm²', imperial: false },
    { value: 'KM2', label: 'km²', imperial: false }, { value: 'IN2', label: 'in²', imperial: true },
    { value: 'FT2', label: 'ft²', imperial: true }, { value: 'YD2', label: 'yd²', imperial: true },
    { value: 'MI2', label: 'mi²', imperial: true }
  ],
  'VOLUME': [
    { value: 'MM3', label: 'mm³' }, { value: 'CM3', label: 'cm³' },
    { value: 'DM3', label: 'dm³' }, { value: 'M3', label: 'm³' },
    { value: 'KM3', label: 'km³' }, { value: 'IN3', label: 'in³', imperial: true },
    { value: 'FT3', label: 'ft³', imperial: true }, { value: 'YD3', label: 'yd³', imperial: true },
    { value: 'MI3', label: 'mi³', imperial: true }, { value: 'L', label: 'L' },
    { value: 'IMP_GAL', label: 'imp gal', imperial: true }, { value: 'US_GAL', label: 'US gal', imperial: true }
  ],
  'CURRENCY': [
    { value: 'EUR', label: '€' }, { value: 'USD', label: '$' },
    { value: 'GBP', label: '£' }, { value: 'TRY', label: '₺' },
    { value: 'CHF', label: '₣' }
  ],
  'SOUND_PRESSURE': [
    { value: 'DB', label: 'dB' }
  ],
  'LUMINANCE': [
    { value: 'CD_M2', label: 'cd/m²' }
  ],
  'TEMPERATURE': [
    { value: 'TF', label: '°F', imperial: true },
    { value: 'TC', label: '°C', imperial: false }
  ],
  'ANGLE': [
    { value: 'DEGREE', label: '°' }
  ],
  'PERCENTAGE': [
    { value: 'PC', label: '%' }
  ],
  'HUMIDITY': [
    { value: 'G_M3', label: 'g/m³' }
  ],
  'VELOCITY': [
    { value: 'M_S', label: 'm/s' }
  ],
  'THERMAL_TRANSMITTANCE': [
    { value: 'W_M2K', label: 'W/(m²K)' },
    { value: 'M2K_W', label: 'm²K/W' }
  ],
  'POWER': [
    { value: 'W', label: 'W' }
  ],
  'WEIGHT': [
    { value: 'MG', label: 'mg', imperial: false },
    { value: 'G', label: 'g', imperial: false },
    { value: 'KG', label: 'kg', imperial: false },
    { value: 'T', label: 't', imperial: false },
    { value: 'OZ', label: 'oz', imperial: true },
    { value: 'LBS', label: 'lbs', imperial: true },
    { value: 'ST', label: 'st', imperial: true },
    { value: 'LT', label: 'lt', imperial: true }
  ],
  'ENERGY': [
    { value: 'KWH', label: 'kWh' }
  ]
};

/**
 * Returns the label for the specified unit.
 * @param {*} unit 
 * @param {*} unitFamily
 * @returns 
 */
export const getUnitLabel = (unit, unitFamily) => {
  if (unitFamily) {
    return UNIT_CHOICES?.[unitFamily]?.find(v => v.value === unit)?.label ?? '';
  } else {
    return Object.values(UNIT_CHOICES).flat().find(v => v.value === unit)?.label ?? '';
  }

};

export const getDefaultUnit = (unitFamily, measurementType) => {
  if (measurementType === 'imperial') {
    switch (unitFamily) {
      case 'DISTANCE':
        return 'FT';
      case 'AREA':
        return 'FT2';
      case 'VOLUME':
        return 'FT3';
      case 'CURRENCY':
        return 'USD';
      case 'SOUND_PRESSURE':
        return 'DB';
      case 'LUMINANCE':
        return 'CD_M2';
      case 'TEMPERATURE':
        return 'TF';
      case 'ANGLE':
        return 'DEGREE';
      case 'PERCENTAGE':
        return 'PC';
      case 'HUMIDITY':
        return 'G_M3';
      case 'VELOCITY':
        return 'M_S';
      case 'THERMAL_TRANSMITTANCE':
        return 'W_M2K';
      case 'POWER':
        return 'W';
      case 'WEIGHT':
        return 'LBS';
      case 'ENERGY':
        return 'KWH';
    }
  } else {
    switch (unitFamily) {
      case 'DISTANCE':
        return 'M';
      case 'AREA':
        return 'M2';
      case 'VOLUME':
        return 'M3';
      case 'CURRENCY':
        return 'EUR';
      case 'SOUND_PRESSURE':
        return 'DB';
      case 'LUMINANCE':
        return 'CD_M2';
      case 'TEMPERATURE':
        return 'TC';
      case 'ANGLE':
        return 'DEGREE';
      case 'PERCENTAGE':
        return 'PC';
      case 'HUMIDITY':
        return 'G_M3';
      case 'VELOCITY':
        return 'M_S';
      case 'THERMAL_TRANSMITTANCE':
        return 'W_M2K';
      case 'POWER':
        return 'W';
      case 'WEIGHT':
        return 'KG';
      case 'ENERGY':
        return 'KWH';
    }
  }
};

const inFactor = 0.0254;
const ftFactor = 0.3048;
const ydFactor = 0.9144;
const miFactor = 1609.344;
const impGalFactor = 4.54609;
const usGalFactor = 3.78541;

/**
 * The conversion-values from the unit to the base-unit (m, m^2, m^3 or g).
 */
const conversionValues = {
  "MM": 0.001,
  "CM": 0.01,
  "DM": 0.1,
  "M": 1,
  "KM": 1000,
  "IN": inFactor,
  "FT": ftFactor,
  "YD": ydFactor,
  "MI": miFactor,
  "MM2": 0.000001,
  "CM2": 0.0001,
  "DM2": 0.01,
  "M2": 1,
  "KM2": 1000000,
  "IN2": inFactor ** 2,
  "FT2": ftFactor ** 2,
  "YD2": ydFactor ** 2,
  "MI2": miFactor ** 2,
  "MM3": 0.000000001,
  "CM3": 0.000001,
  "DM3": 0.001,
  "M3": 1,
  "KM3": 1_000_000_000,
  "IN3": inFactor ** 3,
  "FT3": ftFactor ** 3,
  "YD3": ydFactor ** 3,
  "MI3": miFactor ** 3,
  "L": 0.001,
  "IMP_GAL": impGalFactor,
  "US_GAL": usGalFactor,
  "MG": 0.001,
  "G": 1,
  "KG": 1_000,
  "T": 1_000_000,
  "OZ": 28.3495,
  "LBS": 453.592,
  "ST": 907_184.7,
  "LT": 1_016_046.9
};

export const convertValue = (value, unitFrom, unitTo) => {
  if (unitFrom === unitTo) return value;

  if (unitFrom === 'TF' || unitFrom === 'TC') {
    if (unitFrom === 'TF' && unitTo === 'TC') {
      return value * 1.8 + 32;
    } else if (unitFrom === 'TC' && unitTo === 'TF') {
      return (value - 32) / 1.8;
    }
    return value;
  }

  let toBaseFactor = conversionValues[unitFrom];
  if (toBaseFactor === undefined) {
    // simply return the current value, no conversion possible
    return value;
  }
  let conversionFactor = conversionValues[unitTo];
  if (conversionFactor === undefined) {
    // no conversion possible, return original value
    return value;
  }
  return value * toBaseFactor / conversionFactor;
};

export const getCorrespondingUnit = (unit, measurementType) => {
  if (measurementType === 'imperial') {
    switch (unit) {
      // Distance
      case 'MM': return 'IN';
      case 'CM': return 'IN';
      case 'DM': return 'IN';
      case 'M': return 'FT';
      case 'KM': return 'MI';

      // Area
      case 'MM2': return 'IN2';
      case 'CM2': return 'IN2';
      case 'DM2': return 'IN2';
      case 'M2': return 'FT2';
      case 'KM2': return 'MI2';

      // Volume
      case 'MM3': return 'IN3';
      case 'CM3': return 'IN3';
      case 'DM3': return 'IN3';
      case 'M3': return 'FT3';
      case 'KM3': return 'MI3';
      case 'L': return 'US_GAL';

      // TEMPERATURE
      case 'TC': return 'TF';

      // WEIGHT
      case 'MG': return 'OZ';
      case 'G': return 'OZ';
      case 'KG': return 'LBS';
      case 'T': return 'LT';

      default:
        return unit;
    }
  } else {
    switch (unit) {
      // Distance
      case 'IN': return 'CM';
      case 'FT': return 'M';
      case 'YD': return 'M';
      case 'MI': return 'KM';

      // Area
      case 'IN2': return 'CM2';
      case 'FT2': return 'M2';
      case 'YD2': return 'M2';
      case 'MI2': return 'KM2';

      // Volume
      case 'IN3': return 'CM3';
      case 'FT3': return 'M3';
      case 'YD3': return 'M3';
      case 'MI3': return 'KM3';
      case 'IMP_GAL': return 'L';
      case 'US_GAL': return 'L';

      // TEMPERATURE
      case 'TF': return 'TC';

      // WEIGHT
      case 'OZ': return 'G';
      case 'LBS': return 'KG';
      case 'ST': return 'T';
      case 'LT': return 'T';

      default:
        return unit;
    }

  }
}

export const VERSION = pkg.version;